import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { useSelector, useDispatch } from 'react-redux';
import { loadTestPerformanceAsync, selectTestPerformance } from '../dashboardSlice';
import { ChartCard } from './ChartCard';
import { MemoChart } from './MemoChart';
import { MedicalServiceService } from '../../../service/MedicalServiceService';
import { useThemeVersion, getStoredColorMode } from '../../../useTheme';
import { categoryColor } from '../categoryColors';

const medicalServiceService = new MedicalServiceService();

const serviceItemTemplate = (option) => (
    <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{option.label}</span>
);

const testLabel = (item) => {
    const title = item.testTitle || `Prova ${item.testId}`;
    return item.testYear ? `${title} (${item.testYear})` : title;
};

export const TestPerformanceChart = () => {
    const dispatch = useDispatch();
    const { data, status, error, filters } = useSelector(selectTestPerformance);
    const [allServices, setAllServices] = useState([]);
    const loading = status === 'loading';
    // getComputedStyle abaixo só lê o CSS de novo se essa memo recalcular —
    // sem isso, trocar de tema depois de montado deixa a cor antiga presa.
    const themeVersion = useThemeVersion();

    useEffect(() => {
        medicalServiceService.getMedicalServices(0, 0, 500, 'name', 1)
            .then(res => setAllServices(res.data || []))
            .catch(() => setAllServices([]));
    }, []);

    // UF condiciona o serviço (uma UF só mostra serviços daquela UF) — a
    // lista completa fica em allServices, filtrada aqui pela UF ativa.
    const serviceOptions = useMemo(() => {
        const filtered = filters?.uf
            ? allServices.filter(s => s.state === filters.uf)
            : allServices;
        return filtered.map(s => ({ label: `${s.name} (${s.state})`, value: s.id }));
    }, [allServices, filters?.uf]);

    const ufOptions = useMemo(() => {
        const states = Array.from(new Set(allServices.map(s => s.state).filter(Boolean))).sort();
        return states.map(state => ({ label: state, value: state }));
    }, [allServices]);

    const applyFilters = (newFilters) => {
        dispatch(loadTestPerformanceAsync({ ...filters, ...newFilters }));
    };
    const onReload = () => applyFilters({});

    const quizChartData = useMemo(() => {
        if (!Array.isArray(data) || !data.length) {
            return { labels: [], datasets: [] };
        }
        return {
            labels: data.map(testLabel),
            datasets: [{
                label: 'Quiz — % de Acerto',
                data: data.map(item => item.quizCorrectPercentage),
                backgroundColor: categoryColor('test', getStoredColorMode())
            }]
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, themeVersion]);

    const practiceTestChartData = useMemo(() => {
        if (!Array.isArray(data) || !data.length) {
            return { labels: [], datasets: [] };
        }
        return {
            labels: data.map(testLabel),
            datasets: [{
                label: 'Simulado — % de Acerto',
                data: data.map(item => item.practiceTestCorrectPercentage),
                backgroundColor: categoryColor('practiceTest', getStoredColorMode())
            }]
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, themeVersion]);

    const chartOptions = useMemo(() => ({
        indexAxis: 'y',
        scales: {
            x: {
                min: 0,
                max: 100,
                ticks: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--text-color')
                }
            },
            y: {
                ticks: {
                    color: getComputedStyle(document.documentElement).getPropertyValue('--text-color')
                }
            }
        },
        plugins: {
            legend: false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [themeVersion]);

    return (
        <>
            <div className="flex flex-wrap gap-3 mb-3">
                <div className="flex flex-column gap-1">
                    <label htmlFor="test-perf-uf" className="text-xs text-color-secondary">UF</label>
                    <Dropdown
                        inputId="test-perf-uf"
                        value={filters?.uf ?? null}
                        options={ufOptions}
                        onChange={(e) => applyFilters({ uf: e.value, serviceId: null })}
                        placeholder="Filtrar por UF"
                        showClear
                        className="w-full md:w-15rem"
                    />
                </div>
                <div className="flex flex-column gap-1">
                    <label htmlFor="test-perf-service" className="text-xs text-color-secondary">Serviço</label>
                    <Dropdown
                        inputId="test-perf-service"
                        value={filters?.serviceId ?? null}
                        options={serviceOptions}
                        onChange={(e) => applyFilters({ serviceId: e.value })}
                        placeholder="Filtrar por serviço"
                        showClear
                        filter
                        className="w-full md:w-15rem"
                        panelStyle={{ width: '20rem' }}
                        itemTemplate={serviceItemTemplate}
                    />
                </div>
                <div className="flex align-items-center gap-2 align-self-end pb-2">
                    <InputSwitch
                        inputId="test-perf-active-only"
                        checked={!!filters?.activeOnly}
                        onChange={(e) => applyFilters({ activeOnly: e.value })}
                        disabled={loading}
                    />
                    <label htmlFor="test-perf-active-only" className="text-xs text-color-secondary">Somente ativos</label>
                </div>
            </div>
            <ChartCard
                title="Quiz — Desempenho por Prova"
                loading={loading}
                error={error}
                onReload={onReload}
            >
                <MemoChart data={quizChartData} options={chartOptions} />
            </ChartCard>
            <div className="mt-3">
                <ChartCard
                    title="Simulado — Desempenho por Prova"
                    loading={loading}
                    error={error}
                    onReload={onReload}
                >
                    <MemoChart data={practiceTestChartData} options={chartOptions} />
                </ChartCard>
            </div>
        </>
    );
};
